/**
 * Explore's first step: which data source to read - and the same step in the
 * dashboard's chart builder, which borrows Explore's editor and so its source.
 *
 * Every source the server lists is here, usable or not - one that is not says
 * why on hover, rather than being missing. Folded by default, with the active
 * source named in the header.
 *
 * Switching is `switchDataSource`: the same reset whoever asks. A host that
 * cares (Explore moves to the Plot tab for a source without history) listens
 * for `data-source-changed`.
 */

import { html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeDataSource,
  allColumns,
  selectedCols,
  selectedShip,
  shipDateRange,
  ships,
  type DataSource,
} from '../../state/store.ts';
import {
  DEFAULT_SOURCE,
  dataSources,
  isSelectable,
  sourceHasHistory,
  sourceLabel,
  unselectableReason,
} from '../../state/data-sources.ts';
import { setTimeAxis } from '../../state/plot-state.ts';
import { hidePeriodComparisonDisplay, rerenderChartFromLast } from '../plot/plot-actions.ts';
import {
  periodComparison,
  resumeSuspendedPeriodComparison,
  stopPeriodComparison,
} from '../../state/period-comparison.ts';
import { applyDatePreset } from './date-presets.ts';
import { loadColumns, loadShips, pruneFiltersForShip } from './ship-data.ts';
import { prunePlotColumns, syncToUrl } from '../../state/url-sync.ts';
import type { DatePreset } from '../../state/store.ts';

export interface SwitchOptions {
  /** The window a source without history opens on. Explore: Last 1 h. */
  livePreset?: DatePreset;
  /**
   * The window to put back when switching to a source with history, or none to
   * keep the one on screen. The chart builder passes the dashboard's.
   */
  historyWindow?: () => void;
}

/**
 * Read another source: reset ship, columns and selection, reload the ships it
 * has, and keep the ship when it has it too. Plot picks the new source does
 * not have are dropped. A source without history opens on
 * a short window; period comparison pauses until a source with history.
 */
export async function switchDataSource(ds: DataSource, options: SwitchOptions = {}): Promise<void> {
  if (ds === activeDataSource.value) return;
  // Remember the ship across the switch; columns are reloaded fresh since
  // the two sources rarely share the same schema.
  const prevShip = selectedShip.value;
  const history = sourceHasHistory(ds);
  // Period comparison spans months; a source with only a rolling window
  // cannot hold it. Paused, not lost: it comes back with a source that can.
  if (periodComparison.value && !history) {
    stopPeriodComparison(`Period comparison paused: ${sourceLabel(ds)} has no history.`);
    hidePeriodComparisonDisplay(`Return to ${sourceLabel(DEFAULT_SOURCE)} to restore it.`);
  }
  activeDataSource.set(ds);
  try {
    localStorage.setItem('datasource', ds);
  } catch {
    /* remembered only for this page */
  }
  document.body.dataset.datasource = ds;
  setTimeAxis();

  if (!history) applyDatePreset(options.livePreset ?? '1h');
  else options.historyWindow?.();

  // Reset column/selection state, then reload ships for the new source.
  selectedShip.set(null);
  allColumns.set([]);
  selectedCols.set(new Set());
  shipDateRange.set(null);
  await loadShips();

  // Re-select the same ship if the new source has it; loadColumns refetches
  // its columns and clears the (likely mismatched) column selection.
  const keptShip = !!prevShip && ships.value.includes(prevShip);
  if (keptShip) await loadColumns(prevShip);
  // Plot picks and filters name the old source's columns. Keep the ones the
  // new schema has too; with no ship there is no schema, so none survive.
  prunePlotColumns();
  pruneFiltersForShip();
  syncToUrl();
  if (keptShip && history && resumeSuspendedPeriodComparison()) rerenderChartFromLast();
}

@customElement('data-source-step')
export class DataSourceStep extends LightElement {
  private store = new StoreController(this, [activeDataSource, dataSources]);

  /** How a switch made here behaves; see `SwitchOptions`. */
  @property({ attribute: false }) options: SwitchOptions = {};

  @state() private open = false;

  private async pick(ds: DataSource): Promise<void> {
    await switchDataSource(ds, this.options);
    this.dispatchEvent(new CustomEvent('data-source-changed', { detail: ds, bubbles: true, composed: true }));
  }

  override render(): TemplateResult {
    const ds = activeDataSource.value;
    const active = dataSources.value.find((src) => src.id === ds);
    return html`<div class="card explore-query data-source-step">
      <div
        class="section-header"
        role="button"
        aria-expanded=${this.open ? 'true' : 'false'}
        @click=${() => (this.open = !this.open)}
      >
        <span class="arrow ${this.open ? 'open' : ''}">▶</span>
        <span>Data source</span>
        <span class="count">${active?.label ?? ds}</span>
      </div>
      <div class="section-body ${this.open ? '' : 'collapsed'}">
        <div class="seg ds-seg" role="radiogroup" aria-label="Data source">
          ${dataSources.value.map((src) => {
            const usable = isSelectable(src);
            const on = ds === src.id;
            // aria-disabled, not disabled: a disabled button gets no hover, and
            // the hover is where it says why.
            return html`<button
              class="seg-btn ${on ? 'active' : ''}"
              role="radio"
              aria-checked=${on ? 'true' : 'false'}
              aria-disabled=${usable ? 'false' : 'true'}
              title=${unselectableReason(src) ?? src.description}
              data-source=${src.id}
              @click=${() => {
                if (usable) void this.pick(src.id);
              }}
            >
              ${src.label}
            </button>`;
          })}
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'data-source-step': DataSourceStep;
  }
}
